import { memo, useEffect, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { v4 as uuid } from "uuid";

import { Button, Modal } from "@virtual-brain-lib/frontend-design-system/ui";
import { IMAGE_PNG_MIME_TYPE, IMAGE_SVG_XML_MIME_TYPE } from "@virtual-brain-lib/types";

import MermaidRenderer from "./MermaidRenderer";
interface MermaidProps {
	readonly mermaidCode: string;
	readonly isWriting?: boolean;
}

const SCALE_FACTOR = 10; // approximately 500 dpi

function MermaidWrapper({ mermaidCode, isWriting }: MermaidProps) {
	const [fullscreen, setFullscreen] = useState<boolean>(false);
	const [id, setId] = useState<string>("");

	useEffect(() => {
		setId(`m-${uuid()}`);
	}, [mermaidCode, fullscreen]);

	const handleFullscreen = () => {
		setFullscreen(!fullscreen);
	};

	const handleExportPNG = (containerId: string) => {
		const svg = document.querySelector(`#${containerId} > svg`)!;

		if (!svg) {
			return;
		}

		const bbox = svg.getBoundingClientRect();
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		const loader = new Image();

		canvas.width = bbox.width * SCALE_FACTOR;
		canvas.height = bbox.height * SCALE_FACTOR;

		loader.onload = () => {
			ctx?.drawImage(loader, 0, 0, canvas.width, canvas.height);

			canvas.toBlob((blob) => {
				if (!blob) {
					return;
				}

				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");

				a.href = url;
				a.download = `diagram-${containerId}.png`;
				a.click();
				URL.revokeObjectURL(url);
			}, IMAGE_PNG_MIME_TYPE);
		};

		const svgData = new XMLSerializer().serializeToString(svg);

		loader.src = `data:${IMAGE_SVG_XML_MIME_TYPE},${encodeURIComponent(svgData)}`;
	};

	return (
		<div>
			{isWriting ? (
				<div className="flex items-center justify-center rounded-md border border-primary p-20 font-sans">
					Diagram loading...
				</div>
			) : (
				<>
					<Modal
						isOpen={fullscreen}
						title="Diagram Explorer"
						size="2xl"
						close={() => {
							setFullscreen(false);
						}}
					>
						<TransformWrapper>
							{({ zoomIn, zoomOut, resetTransform }) => (
								<>
									<TransformComponent
										contentStyle={{
											height: "100%",
											width: "100%",
											minWidth: "100%",
											minHeight: "100%",
										}}
										wrapperStyle={{
											height: "100%",
											width: "100%",
											minWidth: "100%",
											minHeight: "100%",
										}}
										wrapperClass="border border-primary-border rounded-md"
									>
										<div className="h-[65dvh] w-full p-10">
											<MermaidRenderer
												containerId={id}
												mermaidCode={mermaidCode}
											/>
										</div>
									</TransformComponent>
									<div className="mt-4 flex gap-2">
										<Button
											leftIcon="download"
											onClick={() => {
												handleExportPNG(id);
											}}
										>
											Export PNG
										</Button>
										<Button
											leftIcon="plus"
											onClick={() => {
												zoomIn();
											}}
										>
											Zoom
										</Button>
										<Button
											leftIcon="minus"
											onClick={() => {
												zoomOut();
											}}
										>
											Zoom
										</Button>
										<Button
											leftIcon="retry"
											onClick={() => {
												resetTransform();
											}}
										>
											Reset
										</Button>
										<Button
											disabled
											leftIcon="move"
											tooltip={{
												children: "Click and drag to move, scroll to zoom.",
											}}
										>
											Move
										</Button>
									</div>
								</>
							)}
						</TransformWrapper>
					</Modal>
					<div className="relative rounded-md border border-primary-border p-10">
						<MermaidRenderer
							containerId={id}
							mermaidCode={mermaidCode}
							className="min-h-12.5"
						/>
						<Button
							className="absolute top-1 left-1 z-20"
							leftIcon="moveDiagonal"
							onClick={handleFullscreen}
						/>
						<Button
							className="absolute top-10 left-1 z-20"
							leftIcon="download"
							onClick={() => {
								handleExportPNG(id);
							}}
						/>
					</div>
				</>
			)}
		</div>
	);
}

export default memo(MermaidWrapper);
